import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { LogIn, Smartphone, Briefcase } from 'lucide-react';

/**
 * LoginButton — now presents a "Student / Instructor & Admin" choice before
 * authenticating, since students sign in via Cognito (phone + SMS OTP) while
 * staff continue through Auth0 Universal Login unchanged.
 *
 * Drop-in compatible: every existing usage of <LoginButton /> gets this
 * choice automatically — no changes needed anywhere else that renders it.
 */
export default function LoginButton() {
  const { loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close the dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleStaffLogin = () => {
    setOpen(false);
    loginWithRedirect({
      appState: {
        // After Auth0 redirects back, onRedirectCallback in index.js
        // restores the user to this page instead of the homepage.
        returnTo: window.location.pathname + window.location.search
      }
    });
  };

  const handleStudentLogin = () => {
    setOpen(false);
    navigate('/student-login');
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="btn-login"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <LogIn size={20} />
        Log In
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 0.5rem)',
            right: 0,
            minWidth: '15rem',
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '0.75rem',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            padding: '0.5rem',
            zIndex: 50
          }}
        >
          <p style={{
            margin: '0.35rem 0.6rem 0.5rem',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: '#9ca3af',
            textTransform: 'uppercase',
            letterSpacing: '0.04em'
          }}>
            Sign in as
          </p>

          <button
            role="menuitem"
            onClick={handleStudentLogin}
            style={choiceBtnStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f3ff')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <Smartphone size={18} color="#7c3aed" />
            <span>
              <span style={{ display: 'block', fontWeight: 600, color: '#111827', fontSize: '0.9rem' }}>
                Student
              </span>
              <span style={{ display: 'block', fontSize: '0.78rem', color: '#6b7280' }}>
                Sign in with your phone number
              </span>
            </span>
          </button>

          <button
            role="menuitem"
            onClick={handleStaffLogin}
            style={choiceBtnStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#eff6ff')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <Briefcase size={18} color="#2563eb" />
            <span>
              <span style={{ display: 'block', fontWeight: 600, color: '#111827', fontSize: '0.9rem' }}>
                Instructor / Admin
              </span>
              <span style={{ display: 'block', fontSize: '0.78rem', color: '#6b7280' }}>
                Sign in with email
              </span>
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

const choiceBtnStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.7rem',
  width: '100%',
  padding: '0.6rem 0.6rem',
  border: 'none',
  borderRadius: '0.5rem',
  background: 'transparent',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'inherit'
};